import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Plus, Search, User, X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '../../../ui/Modal';
import Button from '../../../ui/Button';
import Input from '../../../ui/Input';
import { getContactsRequest } from '../../../../store/contacts/contacts.action';
import {
  selectContacts,
  selectContactsLoading,
} from '../../../../store/contacts/contacts.selector';

interface EventAttribute {
  id: string;
  attribute: string;
  value: string;
}

export interface SendEventModalSubmitData {
  identifier: { id: string };
  eventName: string;
  attributes: Array<{ attribute: string; value: string }>;
}

interface SendEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEventName?: string;
  onSend: (data: SendEventModalSubmitData) => void;
  sendEventLoading?: boolean;
  sendEventError?: string | null;
}

const SendEventModal: React.FC<SendEventModalProps> = ({
  isOpen,
  onClose,
  currentEventName = '',
  onSend,
  sendEventLoading = false,
  sendEventError = null,
}) => {
  const dispatch = useDispatch();
  const contacts = useSelector(selectContacts);
  const contactsLoading = useSelector(selectContactsLoading);

  const [selectedContactId, setSelectedContactId] = useState('');
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [eventName, setEventName] = useState('');
  const [attributes, setAttributes] = useState<EventAttribute[]>([
    { id: crypto.randomUUID(), attribute: '', value: '' },
  ]);

  const contactDropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const timeoutId = setTimeout(() => {
      dispatch(
        getContactsRequest({
          page: 0,
          limit: 10,
          ...(search ? { search } : {}),
        })
      );
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [isOpen, dispatch, search]);

  useEffect(() => {
    if (!isContactDropdownOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (
        contactDropdownRef.current &&
        !contactDropdownRef.current.contains(event.target as Node)
      ) {
        setIsContactDropdownOpen(false);
        setLocalSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isContactDropdownOpen]);

  useEffect(() => {
    if (isContactDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isContactDropdownOpen]);

  useEffect(() => {
    if (isOpen) {
      setEventName(currentEventName);
      return;
    }

    setSelectedContactId('');
    setIsContactDropdownOpen(false);
    setSearch('');
    setLocalSearchTerm('');
    setEventName('');
    setAttributes([{ id: crypto.randomUUID(), attribute: '', value: '' }]);
  }, [isOpen, currentEventName]);

  const filteredContacts = useMemo(() => {
    if (!localSearchTerm) {
      return contacts;
    }

    const query = localSearchTerm.toLowerCase();
    return contacts.filter((contact) => {
      const displayName = contact?.email?.toLowerCase() || '';
      const email = (contact.email || '').toLowerCase();
      return displayName.includes(query) || email.includes(query);
    });
  }, [contacts, localSearchTerm]);

  const selectedContactLabel = useMemo(() => {
    if (!selectedContactId) {
      return 'Select a contact';
    }
    const selectedContact = contacts.find((c) => c._id === selectedContactId);
    return selectedContact ? selectedContact?.email : 'Select a contact';
  }, [contacts, selectedContactId]);

  const addAttributeRow = () => {
    setAttributes((prev) => [
      ...prev,
      { id: crypto.randomUUID(), attribute: '', value: '' },
    ]);
  };

  const removeAttributeRow = (id: string) => {
    setAttributes((prev) => prev.filter((item) => item.id !== id));
  };

  const updateAttributeRow = (
    id: string,
    key: 'attribute' | 'value',
    value: string
  ) => {
    setAttributes((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [key]: value } : item))
    );
  };

  const handleSend = () => {
    const cleanedAttributes = attributes
      .filter((item) => item.attribute.trim() || item.value.trim())
      .map((item) => ({
        attribute: item.attribute.trim(),
        value: item.value.trim(),
      }));

    onSend({
      identifier: { id: selectedContactId },
      eventName: eventName.trim(),
      attributes: cleanedAttributes,
    });
  };

  const isSendDisabled =
    !selectedContactId || !eventName.trim() || sendEventLoading;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Send Event" size="medium">
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="block text-sm font-medium text-foreground">
            Select contact <span className="text-destructive">*</span>
          </label>
          <div className="relative" ref={contactDropdownRef}>
            <button
              type="button"
              onClick={() =>
                !contactsLoading &&
                setIsContactDropdownOpen((prevIsOpen) => !prevIsOpen)
              }
              disabled={contactsLoading}
              className="w-full flex items-center justify-between pl-9 pr-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors hover:border-input bg-background disabled:bg-muted disabled:cursor-not-allowed text-left"
            >
              <div className="flex items-center flex-1 min-w-0">
                <User className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <span
                  className={`truncate ${!selectedContactId ? 'text-muted-foreground' : 'text-foreground'}`}
                >
                  {selectedContactLabel}
                </span>
              </div>
              <ChevronDown
                className={`h-3.5 w-3.5 text-muted-foreground transition-transform flex-shrink-0 ${
                  isContactDropdownOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {isContactDropdownOpen && (
              <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg overflow-hidden">
                <div className="p-2 border-b border-border">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={localSearchTerm}
                      onChange={(e) => {
                        const value = e.target.value;
                        setLocalSearchTerm(value);
                        setSearch(value);
                      }}
                      placeholder="Search contacts..."
                      className="w-full pl-9 pr-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-background"
                    />
                  </div>
                </div>

                <div className="max-h-48 overflow-y-auto scrollbar-thin">
                  {contactsLoading ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                      Loading contacts...
                    </div>
                  ) : filteredContacts.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                      {localSearchTerm
                        ? 'No contacts found'
                        : 'No contacts available'}
                    </div>
                  ) : (
                    filteredContacts.map((contact) => (
                      <button
                        key={contact._id}
                        type="button"
                        onClick={() => {
                          setSelectedContactId(contact._id || '');
                          setIsContactDropdownOpen(false);
                          setLocalSearchTerm('');
                        }}
                        disabled={!contact.email}
                        className={`w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          selectedContactId === contact._id
                            ? 'bg-primary/10 text-foreground font-medium'
                            : 'text-foreground'
                        }`}
                      >
                        {contact.email ||
                          `${contact.firstName} ${contact.lastName}` ||
                          '-'}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <Input
          label="Event Name"
          placeholder="Enter Event Name"
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
          className="text-sm font-medium text-foreground"
          required
        />

        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">
            Event Data
          </label>
          <div className="space-y-2">
            {attributes.map((item) => (
              <div key={item.id} className="flex items-center gap-2">
                <Input
                  placeholder="Event Attribute"
                  value={item.attribute}
                  onChange={(e) =>
                    updateAttributeRow(item.id, 'attribute', e.target.value)
                  }
                  className="flex-1"
                />
                <Input
                  placeholder="Value"
                  value={item.value}
                  onChange={(e) =>
                    updateAttributeRow(item.id, 'value', e.target.value)
                  }
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={() => removeAttributeRow(item.id)}
                  className="p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Remove attribute"
                  disabled={attributes.length === 1}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <Button type="button" variant="outline" onClick={addAttributeRow}>
            <Plus className="w-4 h-4 mr-1" />
            Add attribute
          </Button>
        </div>

        <div className="flex flex-col gap-3 pt-4 border-t border-border">
          {sendEventError && (
            <div className="text-sm text-destructive" role="alert">
              {sendEventError}
            </div>
          )}
          <div className="text-xs text-foreground">
            Note: You need to update the campaign for the latest changes to take
            effect.
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSend}
              disabled={isSendDisabled}
            >
              {sendEventLoading ? 'Sending...' : 'Send event'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SendEventModal;
